import React, { useState, Fragment } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, LabRequest } from '../../types';
import { FlaskConical, CheckCircle, Printer } from 'lucide-react';
const fbcStructure = [{
  category: 'WHITE BLOOD CELLS AND WBC MORPHOLOGY',
  tests: [{
    name: 'White Blood Cells (WBC)',
    range: '4-10',
    unit: 'x10^3/uL'
  }, {
    name: 'Neutrophils Neu %',
    range: '40-70',
    unit: '%'
  }, {
    name: 'Lymphocytes LYMP %',
    range: '20-45',
    unit: '%'
  }, {
    name: 'Monocytes MONO %',
    range: '2-10',
    unit: '%'
  }, {
    name: 'Basophils BASO %',
    range: '<1',
    unit: '%'
  }, {
    name: 'Eosinophils EOS%',
    range: '<6',
    unit: '%'
  }, {
    name: 'Neutrophils NEUT#',
    range: '1.7-7.0',
    unit: 'x10^3/uL'
  }, {
    name: 'Lymphocyte LYM#',
    range: '1.0-3.0',
    unit: 'x10^3/uL'
  }, {
    name: 'Monocyte MON #',
    range: '0.3-0.9',
    unit: 'x10^3/uL'
  }, {
    name: 'Basophils BASO#',
    range: '<0.3',
    unit: 'x10^3/uL'
  }, {
    name: 'Eosinophils EOS#',
    range: '0.05-0.5',
    unit: 'x10^3/uL'
  }]
}, {
  category: 'RED CELLS AND RBC LINE',
  tests: [{
    name: 'Red Blood Cells (RBCs)',
    range: '4.3-5.572',
    unit: 'x10^6/uL'
  }, {
    name: 'Haematocrit',
    range: '38.8-54',
    unit: '%'
  }, {
    name: 'Haemoglobin',
    range: '11.5-18.5',
    unit: 'g/dl'
  }, {
    name: 'MCV',
    range: '81.2-95.1',
    unit: 'fL'
  }, {
    name: 'MCH',
    range: '26.6-33.3',
    unit: 'pg/cell'
  }, {
    name: 'MCHC',
    range: '31.5-36.3',
    unit: 'g/dl'
  }, {
    name: 'RDW-SD',
    range: '35-55',
    unit: 'fL'
  }, {
    name: 'RDW-CV',
    range: '11.5-14.5',
    unit: '%'
  }]
}, {
  category: 'PLATELETS',
  tests: [{
    name: 'Platelets (PLT)',
    range: '100-400',
    unit: 'x10^3/uL'
  }, {
    name: 'PDW',
    range: '9-18.5',
    unit: '%'
  }, {
    name: 'MPV',
    range: '7.65-11.5',
    unit: 'fL'
  }, {
    name: 'P-LCR',
    range: '21-45',
    unit: '%'
  }]
}];
const urinalysisTests = [{
  name: 'LEU',
  fullName: 'Leukocytes'
}, {
  name: 'NIT',
  fullName: 'Nitrites'
}, {
  name: 'URO',
  fullName: 'Urobilinogen'
}, {
  name: 'PRO',
  fullName: 'Protein'
}, {
  name: 'pH',
  fullName: 'pH'
}, {
  name: 'BLO',
  fullName: 'Blood'
}, {
  name: 'SG',
  fullName: 'Specific Gravity'
}, {
  name: 'KET',
  fullName: 'Ketones'
}, {
  name: 'BIL',
  fullName: 'Bilirubin'
}, {
  name: 'GLU',
  fullName: 'Glucose'
}];
const testosteroneTests = [{
  name: 'Testosterone, Total',
  rangeMale: '1.66-8.11',
  rangeFemale: '0.13-1.08',
  unit: 'ng/mL',
  method: 'Immunofluorescence'
}];
export const LaboratoryDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    lab: LabRequest;
  } | null>(null);
  const [results, setResults] = useState<Record<string, string>>({});
  // Find encounters with pending lab requests
  const pendingRequests = encounters.flatMap(enc => enc.labRequests.filter(lab => lab.status === 'Pending').map(lab => ({
    enc,
    lab
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    lab: LabRequest;
  }) => {
    setSelectedRequest(item);
    const initialResults: Record<string, string> = {};
    const hasFBC = item.lab.tests.includes('Full Blood Count') || item.lab.tests.includes('FBC');
    const hasUrinalysis = item.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = item.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    item.lab.tests.forEach(test => {
      if ((test === 'Full Blood Count' || test === 'FBC') && hasFBC) {
        fbcStructure.forEach(cat => {
          cat.tests.forEach(t => {
            initialResults[t.name] = '';
          });
        });
      } else if (test.toLowerCase().includes('urinalysis') && hasUrinalysis) {
        urinalysisTests.forEach(t => {
          initialResults[`UA_${t.name}`] = '';
        });
      } else if (test.toLowerCase().includes('testosterone') && hasTestosterone) {
        testosteroneTests.forEach(t => {
          initialResults[`TESTO_${t.name}`] = '';
        });
      } else {
        initialResults[test] = '';
      }
    });
    if (item.lab.otherTests) initialResults['Others'] = '';
    setResults(initialResults);
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const {
      enc,
      lab
    } = selectedRequest;
    const updatedLab: LabRequest = {
      ...lab,
      status: 'Completed',
      results,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id
    };
    const updatedLabRequests = enc.labRequests.map(l => l.id === lab.id ? updatedLab : l);
    // Check if all labs are completed
    const allLabsCompleted = updatedLabRequests.every(l => l.status === 'Completed');
    const updateData: Partial<Encounter> = {
      labRequests: updatedLabRequests,
      pendingLab: !allLabsCompleted
    };
    // If all labs done and no pending radiology, move back to clinician
    if (allLabsCompleted && !enc.pendingRadiology) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  const handlePrint = () => {
    window.print();
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-852">Patient not found</div>;
    const hasFBC = selectedRequest.lab.tests.includes('Full Blood Count') || selectedRequest.lab.tests.includes('FBC');
    const hasUrinalysis = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('urinalysis'));
    const hasTestosterone = selectedRequest.lab.tests.some(t => t.toLowerCase().includes('testosterone'));
    const otherTests = selectedRequest.lab.tests.filter(t => t !== 'Full Blood Count' && t !== 'FBC' && !t.toLowerCase().includes('urinalysis') && !t.toLowerCase().includes('testosterone'));
    return <div className="space-y-6 pb-20" data-id="element-853">
        <div className="flex justify-between items-center print:hidden sticky top-0 bg-gray-50 z-10 py-4 border-b border-gray-200" data-id="element-854">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-855">
            Enter Laboratory Results
          </h1>
          <div className="flex space-x-2" data-id="element-856">
            <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-857">
              Back to Queue
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-858">
              <Printer className="w-4 h-4 mr-2" data-id="element-859" /> Print Results
            </Button>
            <Button onClick={handleSaveResults} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-860">
              <CheckCircle className="w-5 h-5 mr-2" data-id="element-861" /> Save & Send to Clinician
            </Button>
          </div>
        </div>

        {/* Printable Area */}
        <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8 print:border-none print:shadow-none print:p-0" data-id="element-862">
          {/* Print Header */}
          <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-6" data-id="element-863">
            <div className="flex items-center" data-id="element-864">
              <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-24 w-24 object-contain mr-4" data-id="element-865" />
              <div data-id="element-866">
                <h1 className="text-3xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-867">
                  BMED CARE
                </h1>
                <p className="text-sm italic text-gray-600" data-id="element-868">
                  Maternity Care Made Better
                </p>
              </div>
            </div>
            <div className="text-right" data-id="element-869">
              <p className="font-bold text-xl uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-870">
                LABORATORY REPORT
              </p>
              <p className="text-sm" data-id="element-871">
                <span className="font-semibold" data-id="element-872">Date:</span>{' '}
                {new Date().toLocaleDateString()}
              </p>
              <p className="text-sm" data-id="element-873">
                <span className="font-semibold" data-id="element-874">File No:</span>{' '}
                {patient.bmcFileNumber}
              </p>
            </div>
          </div>

          {/* Patient Details */}
          <div className="border-2 border-black p-4 mb-8 text-sm space-y-3" data-id="element-875">
            <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-876">
              <div className="col-span-8" data-id="element-877">
                <span className="font-semibold" data-id="element-878">Patient Name:</span>{' '}
                {patient.firstName} {patient.lastName}
              </div>
              <div className="col-span-2" data-id="element-879">
                <span className="font-semibold" data-id="element-880">Age:</span> {patient.age}
              </div>
              <div className="col-span-2" data-id="element-881">
                <span className="font-semibold" data-id="element-882">Sex:</span> {patient.sex}
              </div>
            </div>
            <div className="grid grid-cols-12 gap-2" data-id="element-883">
              <div className="col-span-6" data-id="element-884">
                <span className="font-semibold" data-id="element-885">Requested By:</span>{' '}
                {selectedRequest.enc.lastAttendingClinician}
              </div>
              <div className="col-span-6" data-id="element-886">
                <span className="font-semibold" data-id="element-887">Requested On:</span>{' '}
                {new Date(selectedRequest.lab.requestedAt).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Results Section */}
          <div className="space-y-8" data-id="element-888">
            {hasFBC && <div data-id="element-889">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-890">
                  Full Blood Count + Differential (FBC + DC)
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-891">
                  <thead data-id="element-892">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-893">
                      <th className="p-2 text-left font-semibold" data-id="element-894">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-895">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-896">
                        Reference range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-897">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-898">
                    {fbcStructure.map(category => <Fragment key={category.category} data-id="element-899">
                        <tr data-id="element-900">
                          <td colSpan={4} className="p-2 font-bold bg-gray-50 uppercase pt-4" data-id="element-901">
                            {category.category}
                          </td>
                        </tr>
                        {category.tests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-902">
                            <td className="p-2 pl-4" data-id="element-903">{test.name}</td>
                            <td className="p-2" data-id="element-904">
                              <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test.name] || ''} onChange={e => setResults({
                        ...results,
                        [test.name]: e.target.value
                      })} placeholder="Enter value" data-id="element-905" />
                            </td>
                            <td className="p-2 text-gray-600" data-id="element-906">{test.range}</td>
                            <td className="p-2 text-gray-600" data-id="element-907">{test.unit}</td>
                          </tr>)}
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {hasUrinalysis && <div data-id="element-908">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-909">
                  Urinalysis Report
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-910">
                  <thead data-id="element-911">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-912">
                      <th className="p-2 text-left font-semibold w-24" data-id="element-913">Code</th>
                      <th className="p-2 text-left font-semibold" data-id="element-914">Parameter</th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-915">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-916">
                    {urinalysisTests.map(test => <tr key={test.name} className="border-b border-gray-100" data-id="element-917">
                        <td className="p-2 font-bold" data-id="element-918">{test.name}</td>
                        <td className="p-2 text-gray-600" data-id="element-919">{test.fullName}</td>
                        <td className="p-2" data-id="element-920">
                          <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`UA_${test.name}`] || ''} onChange={e => setResults({
                      ...results,
                      [`UA_${test.name}`]: e.target.value
                    })} placeholder="Enter result" data-id="element-921" />
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {hasTestosterone && <div data-id="element-922">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-923">
                  Testosterone
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-924">
                  <thead data-id="element-925">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-926">
                      <th className="p-2 text-left font-semibold" data-id="element-927">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-48" data-id="element-928">
                        Value(s)
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-929">
                        Reference Range
                      </th>
                      <th className="p-2 text-left font-semibold" data-id="element-930">Units</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-931">
                    {testosteroneTests.map(test => <Fragment key={test.name} data-id="element-932">
                        <tr className="border-b border-gray-100" data-id="element-933">
                          <td className="p-2 font-medium" data-id="element-934">{test.name}</td>
                          <td className="p-2" data-id="element-935">
                            <input type="text" className="w-full p-1 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[`TESTO_${test.name}`] || ''} onChange={e => setResults({
                        ...results,
                        [`TESTO_${test.name}`]: e.target.value
                      })} placeholder="Enter value" data-id="element-936" />
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-937">
                            <div data-id="element-938">Males: {test.rangeMale}</div>
                            <div data-id="element-939">Females: {test.rangeFemale}</div>
                          </td>
                          <td className="p-2 text-gray-600" data-id="element-940">{test.unit}</td>
                        </tr>
                        <tr className="border-b border-gray-200 bg-gray-50" data-id="element-941">
                          <td colSpan={4} className="p-2 text-xs text-gray-500 italic" data-id="element-942">
                            Method: {test.method}
                          </td>
                        </tr>
                      </Fragment>)}
                  </tbody>
                </table>
              </div>}

            {(otherTests.length > 0 || selectedRequest.lab.otherTests) && <div data-id="element-943">
                <h3 className="font-bold text-lg mb-2 uppercase border-b border-gray-300 pb-1" data-id="element-944">
                  Other Investigations
                </h3>
                <table className="w-full border-collapse text-sm" data-id="element-945">
                  <thead data-id="element-946">
                    <tr className="bg-gray-100 border-y-2 border-black" data-id="element-947">
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-948">
                        Test Description
                      </th>
                      <th className="p-2 text-left font-semibold w-1/2" data-id="element-949">
                        Result
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-950">
                    {otherTests.map(test => <tr key={test} className="border-b border-gray-100" data-id="element-951">
                        <td className="p-2 font-medium" data-id="element-952">{test}</td>
                        <td className="p-2" data-id="element-953">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results[test] || ''} onChange={e => setResults({
                      ...results,
                      [test]: e.target.value
                    })} placeholder="Enter result..." data-id="element-954" />
                        </td>
                      </tr>)}
                    {selectedRequest.lab.otherTests && <tr className="border-b border-gray-100" data-id="element-955">
                        <td className="p-2 font-medium" data-id="element-956">
                          Others: {selectedRequest.lab.otherTests}
                        </td>
                        <td className="p-2" data-id="element-957">
                          <input type="text" className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-yellow-500 focus:outline-none print:border-none print:bg-transparent print:p-0 font-medium" value={results['Others'] || ''} onChange={e => setResults({
                      ...results,
                      ['Others']: e.target.value
                    })} placeholder="Enter result..." data-id="element-958" />
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>}
          </div>

          {/* Footer Signatures */}
          <div className="mt-16 pt-8 border-t border-gray-300" data-id="element-959">
            <div className="flex justify-between items-end" data-id="element-960">
              <div className="w-64" data-id="element-961">
                <p className="text-sm font-semibold mb-8" data-id="element-962">
                  Laboratory Technologist:
                </p>
                <div className="border-b border-black" data-id="element-963"></div>
                <p className="text-sm mt-2 font-medium" data-id="element-964">
                  {currentUser?.fullName}
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-965">
                  BMED Laboratory Dept
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6 print:hidden" data-id="element-966">
      <div className="flex justify-between items-center" data-id="element-967">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-968">
          Laboratory Dashboard
        </h1>
        <Badge variant="warning" className="text-sm px-3 py-1" data-id="element-969">
          {pendingRequests.length} Pending Requests
        </Badge>
      </div>

      <Card noPadding data-id="element-970">
        <ul className="divide-y divide-gray-200" data-id="element-971">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.lab.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-972">
                  <div className="flex items-center" data-id="element-973">
                    <div className="bg-yellow-100 p-2 rounded-full mr-4" data-id="element-974">
                      <FlaskConical className="h-6 w-6 text-yellow-600" data-id="element-975" />
                    </div>
                    <div data-id="element-976">
                      <p className="text-sm font-medium text-blue-600" data-id="element-977">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-978">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-979">
                        Tests: {item.lab.tests.length} requested
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-yellow-600 hover:bg-yellow-700 text-white" data-id="element-980">
                    Enter Results
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-981">
              <FlaskConical className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-982" />
              <p className="text-lg font-medium text-gray-900" data-id="element-983">
                No pending requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};